import { motion } from "framer-motion";
import { Play } from "lucide-react";
import * as React from "react";

interface TestimonialsSectionProps {
  onCTAClick: () => void;
  className?: string;
}

export default function TestimonialsSection({ onCTAClick }: TestimonialsSectionProps) {
  const videoTestimonials = [
    { video: "/videos/testimonial-1.mp4" },
    { video: "/videos/testimonial-2.mp4" },
    { video: "/videos/testimonial-3.mp4" },
    { video: "/videos/testimonial-4.mp4" },
    { video: "/videos/testimonial-5.mp4" },
    { video: "/videos/testimonial-6.mp4" },
  ];

  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // التنقل التلقائي بين الفيديوهات
  React.useEffect(() => {
    if (!isPlaying) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % videoTestimonials.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [videoTestimonials.length, isPlaying]);

  // تشغيل/إيقاف الفيديو
  const handleVideoClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  //  عند الضغط على thumbnail
  const handleThumbnailClick = (index: number) => {
    setCurrentSlide(index);
    setIsPlaying(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  // إعادة تحميل الفيديو عند تغيير السلايد
  React.useEffect(() => {
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [currentSlide]);

  return (
    <section className="relative py-24 bg-gradient-to-br from-[#000000] via-[#0A1E3F] to-[#000000] overflow-hidden">
      {/* خلفية مضيئة */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#CFAE58] rounded-full blur-[200px] opacity-10"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* العنوان */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            شاهد تجارب ناس <span className="text-[#CFAE58]">بدأوا التحدي قبلك</span> 🎥
          </h2>
        </motion.div>

        {/* الفيديو الرئيسي */}
        <div className="max-w-md mx-auto mb-12">
          <div className="relative">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative aspect-[9/16] rounded-2xl overflow-hidden shadow-2xl shadow-[#CFAE58]/20 border-2 border-[#CFAE58]/30"
            >
              <video
                ref={videoRef}
                src={videoTestimonials[currentSlide].video}
                className="w-full h-full object-cover"
                onClick={handleVideoClick}
                onEnded={() => setIsPlaying(false)}
                playsInline
                preload="metadata"
              />

              {!isPlaying && (
                <>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#000000] via-transparent to-transparent"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      onClick={handleVideoClick}
                      className="bg-[#CFAE58] w-20 h-20 rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform shadow-2xl"
                    >
                      <Play className="w-8 h-8 text-white mr-[-4px]" fill="white" />
                    </div>
                  </div>
                </>
              )}
            </motion.div>

            {/* المصغرات */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
              {videoTestimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  onClick={() => handleThumbnailClick(index)}
                  whileHover={{ scale: 1.05 }}
                  className={`relative aspect-[9/16] rounded-lg overflow-hidden cursor-pointer transition-all ${
                    index === currentSlide
                      ? "border-2 border-[#CFAE58] shadow-lg shadow-[#CFAE58]/30"
                      : "border-2 border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <video
                    src={testimonial.video}
                    preload="metadata"
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-[#000000]/40 flex items-center justify-center">
                    <Play className="w-6 h-6 text-white" fill="white" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* زر CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <p className="text-2xl text-gray-300 mb-6">
            حاب تكون التجربة الجاية؟ سجّل بالتحدي المجاني الآن!
          </p>
  <button
  onClick={onCTAClick}
  className="bg-[#CFAE58] text-white px-10 py-4 rounded-xl hover:bg-[#B89A45] transition-all duration-300 
             shadow-[0_0_25px_rgba(207,174,88,0.9),_0_0_50px_rgba(207,174,88,0.6)] 
             transform hover:scale-105 drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]"
>
  سجّل بياناتك الآن
</button>

        </motion.div>
      </div>
    </section>
  );
}
